// @ts-strict-ignore
import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';
import { Queries } from '@shared/api';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { omitTypeName } from '@shared/util/omitTypeName';
import { Fields } from '../../types';
import { LinkedCard } from '.';

export function useSearchWorkflowEntities(
  relatedCardComponent: RelatedCardComponent,
  fieldValues: Fields,
): {
  keyword: string;
  onSearch(keyword: string): void;
  onAdd(card: API.CardFragment): void;
  searchResults: LinkedCard[];
  reset(): void;
  loading: boolean;
  hasMore: boolean;
} {
  const [keyword, _setKeyword] = useState<string>('');
  const [setKeyword] = useDebouncedCallback(_setKeyword, 100);
  const [searchResults, setSearchResults] = useState<LinkedCard[]>([]);
  const [recentlyAddedCard, setRecentlyAddedCard] = useState<LinkedCard>();
  const workflowTemplate = useWorkflowTemplate(relatedCardComponent.workflowTemplateId);
  const sortByComponents =
    relatedCardComponent.listDefault?.sortByComponents?.map(omitTypeName) ?? [];
  const entityVariables: API.ListWorkflowEntitiesQueryVariables = {
    input: {
      workflowTemplateId: relatedCardComponent.workflowTemplateId,
      limit: 50,
      offset: 0,
      sortByComponents,
      filterByComponents: buildFilterByComponents(
        workflowTemplate.workflowTemplate,
        fieldValues,
        relatedCardComponent.listDefault?.filterByComponents ?? [],
      ),
      omniSearchQuery: !isEmpty(keyword) ? keyword : null,
    },
  };

  const {
    data: listEntities,
    loading: isEntitiesLoading,
    refetch,
  } = useQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables: entityVariables,
      skip: workflowTemplate.loading,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cardResults =
      listEntities?.list?.entities.map<LinkedCard>(entity => ({
        cardId: entity.id,
        name: entity.fields['name'],
      })) ?? [];
    const missingRecentlyAddedCard =
      !isNil(recentlyAddedCard) &&
      isNil(
        cardResults.find(value => {
          return value.cardId === recentlyAddedCard.cardId;
        }),
      );

    if (missingRecentlyAddedCard) {
      cardResults.push(recentlyAddedCard);
    }

    setSearchResults(cardResults);
  }, [listEntities?.list?.entities, recentlyAddedCard]);

  const reset = useCallback(() => _setKeyword(''), []);

  const onAdd = useCallback(
    (card: API.CardFragment) => {
      setRecentlyAddedCard({ cardId: card.id, name: card.fields.name });
      refetch();
    },
    [refetch],
  );

  return {
    keyword,
    reset,
    onSearch: setKeyword,
    onAdd,
    searchResults,
    loading: workflowTemplate.loading || isEntitiesLoading,
    hasMore: listEntities?.list?.hasMore,
  };
}

export function buildFilterByComponentFromValues(
  filteredComponent: API.AnyComponent,
  dynamicComponentId: string,
  fields: Fields,
): API.FilterByComponentFragment {
  if (isTagComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues) || isEmpty(fieldValues)) {
      return null;
    }
    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: null,
        containsString: { values: fieldValues, negate: false },
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        isEmpty: null,
        numberIs: null,
        stringWildcard: null,
      },
    };
  }
  if (isRelatedCardComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues) || isEmpty(fieldValues)) {
      return null;
    }

    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: null,
        containsString: { values: fieldValues.map(fieldValue => fieldValue.id), negate: false },
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        isEmpty: null,
        numberIs: null,
        stringWildcard: null,
      },
    };
  }
  if (isPersonComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues) || isEmpty(fieldValues)) {
      return null;
    }

    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: {
          values: fieldValues,
          negate: false,
        },
        containsString: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        isEmpty: null,
        numberIs: null,
        stringWildcard: null,
      },
    };
  }
  if (isTextComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues) || isEmpty(fieldValues)) {
      return null;
    }
    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: null,
        containsString: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        isEmpty: null,
        numberIs: null,
        stringWildcard: { value: fieldValues },
      },
    };
  }
  if (isDateComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues) || isEmpty(fieldValues)) {
      return null;
    }
    const day = moment(fieldValues);
    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: null,
        containsString: null,
        dateIs: null,
        dateRange: {
          before: day.endOf('day').toISOString(),
          after: day.startOf('day').toISOString(),
        },
        dateTokenIs: null,
        isEmpty: null,
        numberIs: null,
        stringWildcard: null,
      },
    };
  }
  if (isNumberComponent(filteredComponent)) {
    const fieldValues = fields[dynamicComponentId];
    if (isNil(fieldValues)) {
      return null;
    }
    return {
      componentId: filteredComponent.id,
      value: {
        containsCurrentUser: null,
        containsNumber: null,
        containsString: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        isEmpty: null,
        numberIs: { value: fieldValues, operator: 'EQ' },
        stringWildcard: null,
      },
    };
  }
}

export function buildFilterByComponents(
  workflowTemplate: API.WorkflowTemplateFragment,
  fields: Fields,
  filters: API.DynamicFilterByComponentFragment[],
): API.DynamicFilterByComponent[] {
  if (isNil(workflowTemplate)) {
    return [];
  }
  return filters
    .map(filter => {
      if (filter.value.containsCurrentWorkflowTemplateComponentValue === null) {
        return {
          componentId: filter.componentId,
          value: {
            ...filter.value,
          },
        };
      } else {
        const component = workflowTemplate.components.find(
          c => !isNil(c) && c.id === filter.componentId,
        );
        if (isNil(component)) {
          return null;
        }
        return buildFilterByComponentFromValues(
          component,
          filter.value.containsCurrentWorkflowTemplateComponentValue.componentId,
          fields,
        );
      }
    })
    .filter(filter => !isNil(filter));
}
